/**
 * Checkout Page
 * Handles subscription plan selection, payment, and entitlement assignment
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CreditCard, 
  Lock, 
  CheckCircle2, 
  ArrowLeft,
  AlertCircle,
  Building2,
  Mail,
  Phone,
  Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import { entitlementService } from '../services/entitlementService';
import { SUBSCRIPTION_PLANS, getMonthlyPrice, getAnnualPrice } from '../data/subscriptionPlans';
import type { SubscriptionTier, BillingCycle } from '../types/subscription';
import type { AccessLevel } from '../types/entitlement';

export function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { refreshEntitlements } = useEntitlements();
  
  const planParam = searchParams.get('plan') as SubscriptionTier || 'pro';
  const returnUrl = searchParams.get('returnUrl') || '/projects';
  
  const [selectedPlan] = useState<SubscriptionTier>(planParam);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/login?plan=${selectedPlan}&returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  }, [isAuthenticated, navigate, selectedPlan, returnUrl]);
  
  const plan = SUBSCRIPTION_PLANS[selectedPlan];
  const price = billingCycle === 'monthly' 
    ? getMonthlyPrice(selectedPlan)
    : Math.round(getAnnualPrice(selectedPlan) / 12);
  const totalPrice = billingCycle === 'monthly'
    ? getMonthlyPrice(selectedPlan)
    : getAnnualPrice(selectedPlan);
  
  // Map subscription tier to access level
  const tierToAccessLevel = (tier: SubscriptionTier): AccessLevel => {
    const mapping: Record<SubscriptionTier, AccessLevel> = {
      free: 'free',
      pro: 'pro',
      business: 'business',
      enterprise: 'enterprise'
    };
    return mapping[tier];
  };
  
  // Handle free plan (Starter)
  const handleFreePlan = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // No payment needed for free plan
      // Just ensure user has free tier entitlement
      await entitlementService.grantEntitlement({
        userId: user!.id,
        source: 'subscription',
        accessLevel: 'free',
        metadata: {
          reason: 'Free plan selected',
          subscriptionId: `free_${Date.now()}`
        }
      });
      
      await refreshEntitlements();
      
      // Redirect to confirmation
      navigate(`/checkout/confirmation?plan=free&returnUrl=${encodeURIComponent(returnUrl)}`);
      
    } catch (err) {
      setError('Failed to activate free plan. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle paid plan checkout
  const handlePaidCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsProcessing(true);
      setError(null);
      
      // In production, this would:
      // 1. Create Stripe checkout session
      // 2. Redirect to Stripe
      // 3. Handle webhook on success
      // 4. Grant entitlement
      
      // For now, simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Grant entitlement
      const duration = billingCycle === 'monthly' ? 30 : 365;
      await entitlementService.grantEntitlement({
        userId: user!.id,
        source: 'subscription',
        accessLevel: tierToAccessLevel(selectedPlan),
        duration,
        metadata: {
          reason: `${selectedPlan} subscription purchased`,
          subscriptionId: `sub_${Date.now()}`,
          customAttributes: {
            billingCycle,
            amount: totalPrice
          }
        }
      });
      
      await refreshEntitlements();
      
      // Redirect to confirmation
      navigate(`/checkout/confirmation?plan=${selectedPlan}&cycle=${billingCycle}&returnUrl=${encodeURIComponent(returnUrl)}`);
      
    } catch (err) {
      setError('Payment processing failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle enterprise contact form
  const handleEnterpriseContact = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsProcessing(true);
      setError(null);
      
      // In production, send email to sales team
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Redirect to confirmation
      navigate(`/checkout/confirmation?plan=enterprise&contact=true&returnUrl=${encodeURIComponent(returnUrl)}`);
      
    } catch (err) {
      setError('Failed to submit contact form. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (!isAuthenticated || !user) {
    return null; // Will redirect in useEffect
  }
  
  // Free plan - show confirmation
  if (selectedPlan === 'free') {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Activate Free Plan</h1>
              <p className="text-slate-600">Get started with basic features at no cost</p>
            </div>
            
            <div className="border-t border-slate-200 pt-6 mb-6">
              <h3 className="font-semibold text-slate-900 mb-4">What's included:</h3>
              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-900">{error}</span>
              </div>
            )}
            
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/pricing')}
                className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 inline mr-2" />
                Back
              </button>
              <button
                onClick={handleFreePlan}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:bg-slate-400"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />
                    Activating...
                  </>
                ) : (
                  'Activate Free Plan'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Enterprise plan - show contact form
  if (selectedPlan === 'enterprise') {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-10 h-10 text-purple-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Contact Sales</h1>
              <p className="text-slate-600">Let's discuss your enterprise needs</p>
            </div>
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-900">{error}</span>
              </div>
            )}
            
            <form onSubmit={handleEnterpriseContact} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Acme Fire Systems"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Number of Users
                </label>
                <select className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                  <option>10-25</option>
                  <option>26-50</option>
                  <option>51-100</option>
                  <option>100+</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                  <input
                    type="tel"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Tell us about your requirements..."
                />
              </div>
              
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/pricing')}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 inline mr-2" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:bg-slate-400"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Contact Sales'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
  
  // Paid plans (Pro, Business) - show checkout form
  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Order Summary</h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-900">{plan.name}</h3>
                      <p className="text-sm text-slate-600">{billingCycle === 'monthly' ? 'Monthly' : 'Annual'} billing</p>
                    </div>
                    <span className="font-bold text-slate-900">${price}/mo</span>
                  </div>
                  
                  {billingCycle === 'annual' && (
                    <p className="text-sm text-green-600 font-medium">
                      Save ${(getMonthlyPrice(selectedPlan) * 12) - getAnnualPrice(selectedPlan)}/year
                    </p>
                  )}
                </div>
                
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-semibold text-slate-900">Total {billingCycle === 'annual' ? '(billed annually)' : ''}</span>
                    <span className="text-2xl font-bold text-indigo-600">${totalPrice}</span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                      <span>Cancel anytime</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                      <span>Secure payment</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                      <span>Instant activation</span>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Included Features:</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    {plan.features.slice(0, 5).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.features.length > 5 && (
                      <li className="text-indigo-600 font-medium">
                        +{plan.features.length - 5} more features
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Payment Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Payment Information</h2>
              
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-red-900">{error}</span>
                </div>
              )}
              
              {/* Billing Cycle Toggle */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Billing Cycle
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      billingCycle === 'monthly'
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    <div className="font-semibold text-slate-900">Monthly</div>
                    <div className="text-sm text-slate-600">${getMonthlyPrice(selectedPlan)}/month</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('annual')}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      billingCycle === 'annual'
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    <div className="font-semibold text-slate-900">Annual</div>
                    <div className="text-sm text-slate-600">${getAnnualPrice(selectedPlan)}/year</div>
                    <div className="text-xs text-green-600 font-medium mt-1">Save 17%</div>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handlePaidCheckout} className="space-y-6">
                {/* Card Information */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Card Number
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="4242 4242 4242 4242"
                      maxLength={19}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Expiry Date
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="MM/YY"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      CVC
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="123"
                      maxLength={4}
                    />
                  </div>
                </div>
                
                {/* Billing Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Billing Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      required
                      defaultValue={user.email}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>
                
                {/* Security Notice */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center text-sm text-slate-600">
                    <Lock className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>
                      Your payment information is encrypted and secure. We use industry-standard SSL encryption.
                    </span>
                  </div>
                </div>
                
                {/* Submit Buttons */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 inline mr-2" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:bg-slate-400"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5 inline mr-2" />
                        Pay ${totalPrice}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
